import { useTranslation } from "react-i18next";

import {
  formatPostFlightDateLabel,
  formatWindowRange,
  formatWindowRangeFromBounds,
} from "@/features/post-flight/lib/post-flight-list";
import {
  formatPostFlightMoney,
  type PostFlightWindowContextData,
} from "../../lib/post-flight-opportunity-metrics.js";

export type { PostFlightWindowContextData };

function formatCpaHalf(
  value: number | null | undefined,
  missingLabel: string,
): string {
  if (value == null || !Number.isFinite(value)) return missingLabel;
  return formatPostFlightMoney(value);
}

/** Saved double-down context and live list rows share the same date-range copy. */
export function PostFlightWindowContext({
  data,
  listAsOf,
  fallbackWindowDays,
  className = "flex flex-col gap-1 text-[11px] text-muted-foreground",
}: {
  data: PostFlightWindowContextData;
  /** When bounds are missing, compute the inclusive range from list `as_of`. */
  listAsOf?: string | null;
  fallbackWindowDays?: number;
  className?: string;
}) {
  const { t } = useTranslation();
  const snapshotAsOf = data.asOf ?? listAsOf ?? null;
  const range =
    formatWindowRangeFromBounds(data.windowStart, data.windowEnd) ??
    (data.windowDays != null && data.windowDays > 0 && snapshotAsOf
      ? formatWindowRange(snapshotAsOf, data.windowDays)
      : null) ??
    (fallbackWindowDays != null && fallbackWindowDays > 0 && snapshotAsOf
      ? formatWindowRange(snapshotAsOf, fallbackWindowDays)
      : null);
  const asOfLabel = formatPostFlightDateLabel(snapshotAsOf, true);
  const firstSeenLabel = formatPostFlightDateLabel(data.firstSeen, true);
  const missingValue = t("personaOpportunities.postFlightOpportunity.missingValue", {
    defaultValue: "—",
  });

  return (
    <div className={className}>
      {range ? (
        <span className="inline-flex w-fit rounded-sm border border-border bg-muted/40 px-1.5 py-0.5 tabular-nums">
          {range}
        </span>
      ) : null}
      {asOfLabel ? (
        <p>
          {t("personaOpportunities.postFlightOpportunity.dataThrough", {
            date: asOfLabel,
            defaultValue: "Data through {{date}}",
          })}
        </p>
      ) : null}
      {firstSeenLabel ? (
        <p>
          {t("personaOpportunities.postFlightOpportunity.liveSince", {
            date: firstSeenLabel,
            defaultValue: "Live since {{date}}",
          })}
        </p>
      ) : null}
      <p className="tabular-nums">
        {t("personaOpportunities.postFlightOpportunity.cpaHalvesLabel", {
          first: formatCpaHalf(data.cpaFirstHalf, missingValue),
          second: formatCpaHalf(data.cpaSecondHalf, missingValue),
          defaultValue: "CPA, first half / second half of live days: {{first}} / {{second}}",
        })}
      </p>
    </div>
  );
}
